import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInRouterContext, useSearchParams } from 'react-router-dom';
import { api } from '../../../utils/api';
import ChartOfAccounts from '../../Bookkeeping/ChartOfAccounts';
import ManualEntry from '../../Bookkeeping/ManualEntry';
import DraftEntries from '../../Bookkeeping/DraftEntries';
import EntityInfo from '../../Bookkeeping/EntityInfo';
import CompliancePanel from '../../Bookkeeping/CompliancePanel';
import { ClosePeriod, OpenPeriodForm } from '../../Bookkeeping/StatutoryBooks';
import { JOURNAL_CODES, JOURNAL_KEY, JournalBookView, bookTitle, type Book } from '../JournalBook';
import { LedgerAccountsView } from '../LedgerAccounts';
import { TrialBalanceView } from '../TrialBalance';
import { AutoPostStatus } from '../autoPost';
import type { AwCtx } from '../ctx';
import { isReadOnlySeat } from './fixedAssets/shared';
import { btn, card, dmy, errBox, note, num, openBlob, pillGreen, pillGrey, sectionHead, table, td, th } from '../ui';

// ── Accounting panes (owner 2026-10-13) ─────────────────────────────────────────────────────────
// Each submenu mounts the panel StatutoryBooks already mounted, with the same props, under the
// shell's shared period. Books and ledgers stay what they are on the server — printable documents —
// and the General ledger and Trial balance open a screen over the same posted lines (see LedgerPane
// below), with the print HTML and the PDF a click away from it.

export function ChartPane({ ctx }: { ctx: AwCtx }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <ChartOfAccounts clientId={ctx.clientId} defaultOpen />
      {/* The four mapping cards are gone — see the note in Bookkeeping/StatutoryBooks.tsx. A
          service's account is set on Sales › Products & accounts; a line's is set on the line. */}
    </div>
  );
}

/**
 * The five daily journals, as the way INTO each book (COA-C2, owner 2026-10-19).
 *
 * ⛔ Clicking a card used to fetch the statutory PDF and window.open() it, which most browsers drop
 * straight into the Downloads folder — the customer's own complaint, in the plainest language of
 * any document they sent. A card now opens that book's entries on screen (CompanyWorkspace/
 * JournalBook.tsx); the PDF is still one click away, demoted to a button in that page's toolbar.
 *
 * ⛔ A BANK BOOK IS ONE VOLUME PER ACCOUNT NUMBER — the Act's shape, and why /available groups by
 * (source_book, bank_account_id) and this grid draws one card per account. The card's own account
 * travels with it into JournalBookView, which puts it in the view so the list and both exports read
 * that volume alone. It did not always: GET /journal/entries filtered by `book` and not by account,
 * so a client with two accounts opened a card labelled with ONE number and read BOTH accounts'
 * entries, while the statutory PDF behind the same card showed one. The parameter is the fix.
 *
 * (EXCEPTION, owner 2026-11-02, "Daily Journals Structure": this page opens on five journals, Purchase, Cash Disbursements, Sales,
 * Cash Receipts and General, which replace the Bank and Cash Book cards ON THE SCREEN ONLY. ⛔ The cards above all stay, under the
 * heading "Statutory books": each volume, each bank account, each PDF, the very same routes, so the printed books and the
 * inspection pack do not move by a byte (statutoryPrintHash). The five are a way of reading the same posted entries
 * (server/src/services/journalTypes.ts), each entry on exactly one of them. Bank accounts stay in Banking for reconciliation.)
 */
export function JournalCards({ ctx, open, setOpen }: { ctx: AwCtx; open: Book | null; setOpen: (b: Book | null) => void }) {
  const { t } = useTranslation();
  const [books, setBooks] = useState<Book[] | null>(null);
  const [journals, setJournals] = useState<{ type: string; entries: number }[] | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    api.ledgerAvailable(ctx.periodId).then((d: any) => { if (alive) setBooks((d.books || []).filter((b: Book) => b.kind !== 'gl')); }).catch((e: any) => { if (alive) { setBooks([]); setErr(String(e?.message || e)); } });
    return () => { alive = false; };
  }, [ctx.periodId, ctx.reload]);
  useEffect(() => {
    let alive = true;
    api.ledgerJournals(ctx.periodId).then((d: any) => { if (alive) setJournals(d.journals || []); }).catch((e: any) => { if (alive) { setJournals([]); setErr(String(e?.message || e)); } });
    return () => { alive = false; };
  }, [ctx.periodId, ctx.reload]);
  // A period change can take the open volume away with it (a book with no posted entry is not
  // offered at all), so the page falls back to the cards rather than listing a book that is gone.
  // The five daily journals are always offered, so an open one stays.
  useEffect(() => {
    if (!open || open.journal || books === null) return;
    if (!books.some(b => b.kind === open.kind && b.bankAccountId === open.bankAccountId)) setOpen(null);
  }, [books, open, setOpen]);

  if (books === null || journals === null) return null;
  if (open) return <JournalBookView ctx={ctx} book={open} onBack={() => setOpen(null)} />;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <div style={sectionHead}>{t('pa_sb_h_journals')}</div>
        {err && <div style={{ ...errBox, marginBottom: 8 }}>{err}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))', gap: 8 }}>
          {JOURNAL_CODES.map(j => {
            const entries = journals.find(x => x.type === j)?.entries || 0;
            return (
              <button key={j} data-jl-card={j} title={t('aw_jl_open_journal')}
                style={{ ...card, display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', textAlign: 'left', width: '100%' }}
                onClick={() => setOpen({ kind: '', label: j, bankAccountId: '', entries, journal: j })}>
                <i className="ti ti-notebook" style={{ fontSize: 18, color: 'var(--accent)', flexShrink: 0 }} aria-hidden />
                <span style={{ minWidth: 0, flex: 1 }}>
                  <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{t(JOURNAL_KEY[j])}</span>
                  <span style={{ display: 'block', fontSize: 11, color: 'var(--text-tertiary)' }}>{j} · {t('pa_sb_n_entries', { count: entries })}</span>
                </span>
                <i className="ti ti-chevron-right" style={{ fontSize: 15, color: 'var(--text-tertiary)' }} aria-hidden />
              </button>
            );
          })}
        </div>
        {/* The counts on these cards are POSTED entries — said here as well as inside the book,
            because this is where somebody reads a number and decides whether anything is missing. */}
        <div style={{ ...note, marginTop: 8 }}>{t('aw_jl_posted_only')}</div>
        <div style={{ ...note, marginTop: 4 }}>{t('aw_jl_five_note')}</div>
      </div>
      <div data-jl-statutory>
        <div style={sectionHead}>{t('aw_jl_h_statutory')}</div>
        {!books.length ? <div style={note}>{t('pa_sb_nothing_posted')}</div> : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))', gap: 8 }}>
              {books.map(b => (
                <button key={b.kind + b.bankAccountId} title={t('aw_jl_open_title')}
                  style={{ ...card, display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', textAlign: 'left', width: '100%' }}
                  onClick={() => setOpen(b)}>
                  <i className="ti ti-book" style={{ fontSize: 18, color: 'var(--accent)', flexShrink: 0 }} aria-hidden />
                  <span style={{ minWidth: 0, flex: 1 }}>
                    <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{bookTitle(b)}</span>
                    <span style={{ display: 'block', fontSize: 11, color: 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {b.label} · {t('pa_sb_n_entries', { count: b.entries })}
                    </span>
                  </span>
                  <i className="ti ti-chevron-right" style={{ fontSize: 15, color: 'var(--text-tertiary)' }} aria-hidden />
                </button>
              ))}
            </div>
            <div style={{ ...note, marginTop: 8 }}>{t('aw_jl_statutory_note')}</div>
          </>
        )}
      </div>
    </div>
  );
}

/** Accounting › Fixed assets — batch 9 R4 gave it the three tabs the customer asked for
 *  (panes/fixedAssets/). ⛔ This used to mount the classic module's one-card component; the classic
 *  page still does, and the two screens share every route, so a rule the server keeps holds on both. */
export { FixedAssetsPane } from './fixedAssets';

export function JournalPane({ ctx }: { ctx: AwCtx }) {
  const inRouter = useInRouterContext();
  return inRouter ? <JournalPaneRouted ctx={ctx} /> : <JournalPaneBody ctx={ctx} openFor={null} />;
}
/** (W2 ship) An entries panel's "Record by hand" lands here with the source in the URL (forKind / forId / forLabel):
 *  Manual entries opens its New form made FOR that source, and the URL is cleared so a reload opens nothing. */
function JournalPaneRouted({ ctx }: { ctx: AwCtx }) {
  const [params, setParams] = useSearchParams();
  const fk = params.get('forKind') || '', fi = params.get('forId') || '', fl = params.get('forLabel') || '';
  const [openFor] = useState(() => (fk && fi ? { kind: fk, id: fi, label: fl } : null));
  useEffect(() => {
    if (!fk && !fi) return;
    setParams(prev => { const n = new URLSearchParams(prev); n.delete('forKind'); n.delete('forId'); n.delete('forLabel'); return n; }, { replace: true });
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  return <JournalPaneBody ctx={ctx} openFor={openFor} />;
}
function JournalPaneBody({ ctx, openFor }: { ctx: AwCtx; openFor: { kind: string; id: string; label: string } | null }) {
  // An open book takes the whole page: the entry forms stay one click away behind the card grid,
  // so the list an accountant came here to read is not a strip at the bottom of two panels.
  const [open, setOpen] = useState<Book | null>(null);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {!open && <AutoPostStatus ctx={ctx} />}
      {!open && <ManualEntry clientId={ctx.clientId} periodId={ctx.periodId} periodClosed={ctx.periodClosed} onChanged={ctx.bump} openFor={openFor} />}
      {!open && <DraftEntries clientId={ctx.clientId} periodId={ctx.periodId} periodClosed={ctx.periodClosed} refreshKey={ctx.reload} onPosted={ctx.bump} />}
      <JournalCards ctx={ctx} open={open} setOpen={setOpen} />
    </div>
  );
}

/** Buttons that open a ledger-books PDF in a new tab, with the spinner every download button owes. */
export function BookButtons({ ctx, items }: { ctx: AwCtx; items: { key: string; th: string; glossKey: string; path: string }[] }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const open = async (k: string, path: string) => {
    if (busy) return; setBusy(k); setErr('');
    try { openBlob(await api.ledgerBookUrl(path, ctx.periodId)); } catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {items.map(i => (
          <button key={i.key} onClick={() => open(i.key, i.path)} disabled={!!busy} title={t(i.glossKey)} style={{ ...btn, opacity: busy === i.key ? 0.6 : 1 }}>
            <i className={busy === i.key ? 'ti ti-loader' : 'ti ti-file-type-pdf'} aria-hidden /> {i.th} <span style={{ color: 'var(--text-tertiary)', fontWeight: 500 }}>· {t(i.glossKey)}</span>
          </button>
        ))}
      </div>
      {err && <div style={{ ...errBox, marginTop: 8 }}>{err}</div>}
    </div>
  );
}

/**
 * The General ledger menu, and the Trial balance one (owner 2026-10-19).
 *
 * ⛔ THE GENERAL LEDGER NO LONGER OPENS THE STATUTORY PRINT. It used to render
 * `HtmlViewer base="ledger-books" path="general-ledger"` — the DBD's บัญชีแยกประเภท, which lists
 * every account's movements one under another, 273 accounts of them on production, with nothing to
 * click. That is the customer's complaint by name. The menu now opens the two-level interactive
 * ledger (CompanyWorkspace/LedgerAccounts.tsx): one row per account with four figures, and the
 * account's own movements one click deeper.
 *
 * ⛔ AND NOTHING WAS TAKEN AWAY. The statutory print is reachable FROM that screen — its three PDFs
 * and the same inline print HTML this pane used to show, under "the statutory book" in its toolbar.
 * The print endpoints are untouched, byte for byte: no filter of the new screen reaches them (the
 * suite renders them out of the pinned pre-change commit and compares). The same demotion
 * JournalBook.tsx did to the journal PDF — on screen first, the print one click away.
 *
 * The TRIAL BALANCE stood here as the print until 2026-11-02, on the reasoning of 2026-10-19 that it is a statement of the whole
 * chart and an interactive copy would be a second answer to a question the statutory one already answers.
 * (EXCEPTION, owner 2026-11-02, "Modification — Trial Balance Module": the menu opens the table itself, without a cover, and a reader
 * drills into it. CompanyWorkspace/TrialBalance.tsx.) ⛔ The same demotion as the ledger above, and nothing taken away: the
 * statutory งบทดลอง, with its cover, is one menu away on that screen, on the same route with the same two parameters and the
 * same bytes (statutoryPrintHash). The second answer is kept from disagreeing the way the ledger's is: both read the same posted
 * lines for the period, and where they part, a system OPEN- entry, the screen says by how much.
 */
export function LedgerPane({ ctx, book }: { ctx: AwCtx; book: 'general-ledger' | 'trial-balance' }) {
  if (book === 'general-ledger') return <LedgerAccountsView ctx={ctx} />;
  return <TrialBalanceView ctx={ctx} />;
}

export function PeriodsPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <div style={sectionHead}>{t('aw_periods_title')}</div>
        <div style={{ ...card, padding: 0 }}>
          <table style={table}>
            <thead><tr><th style={th}>{t('aw_col_start')}</th><th style={th}>{t('aw_col_end')}</th><th style={th}>{t('aw_col_status')}</th><th style={{ ...th, textAlign: 'right' }}>{t('aw_col_entries')}</th></tr></thead>
            <tbody>
              {ctx.periods.map(p => (
                <tr key={p.id} style={p.id === ctx.periodId ? { background: 'var(--accent-bg)' } : undefined}>
                  <td style={td}>{dmy(p.start_date)}</td><td style={td}>{dmy(p.end_date)}</td>
                  <td style={td}>{p.status === 'closed' ? <span style={pillGreen}>ปิดบัญชีแล้ว · {t('pa_sb_status_closed')}</span> : <span style={pillGrey}>{t('aw_period_open')}</span>}</td>
                  <td style={num}>{p.entries}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* One open period per client is a database constraint; the form surfaces the server's refusal.
          ⛔ Never for a read-only seat (books login, auditor): its Open is a POST the server refuses,
          and a refusal pages the admins — the shell's own two copies are gated the same way. */}
      {ctx.periods.every(p => p.status === 'closed') && !isReadOnlySeat(ctx) && <OpenPeriodForm clientId={ctx.clientId} onOpened={ctx.refreshPeriods} />}
      <EntityInfo clientId={ctx.clientId} periodClosed={ctx.periodClosed} onChanged={ctx.bump} />
    </div>
  );
}

export function ClosingPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  // ⛔ Closing is a staff act: a read-only seat is not handed ClosePeriod (its preview is the first
  // step of a POST the server refuses, and a refusal pages the admins). The compliance card below
  // gates its own writes, because it is mounted from two other places as well.
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {ctx.periodClosed
        ? <div style={{ ...card, color: 'var(--text-tertiary)', fontSize: 12.5 }}><i className="ti ti-lock" aria-hidden /> {t('aw_period_closed_msg')}</div>
        : !isReadOnlySeat(ctx) && <div style={card}><ClosePeriod periodId={ctx.periodId} onClosed={ctx.refreshPeriods} /></div>}
      <CompliancePanel clientId={ctx.clientId} periodId={ctx.periodId} />
    </div>
  );
}
